// ============================================================================
// PRODUCT RENTAL SYSTEM
// ============================================================================
// CONFIGURATION - Reads from global config set in theme.liquid
const PRODUCT_RENTAL_API_BASE_URL = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
// ============================================================================

console.log('🚀 ProductRental.js loaded, API_BASE_URL:', PRODUCT_RENTAL_API_BASE_URL);

// Global function called from liquid templates
window.initializeRentalButton = function(productId, variantId, customerId) {
  console.log('🔗 initializeRentalButton called with:', { productId, variantId, customerId });
  
  if (customerId && customerId !== 'null') {
    new window.ProductRental(variantId, customerId);
  } else {
    console.log('⚠️ No customer ID provided, skipping rental initialization');
  }
};

// Main ProductRental class
window.ProductRental = class ProductRental {
  constructor(variantId, customerId) {
    console.log('🏗️ ProductRental constructor called with:', { variantId, customerId });
    
    this.variantId = variantId;
    this.customerId = customerId;
    // Use the actual product ID from the button, not extracted from variantId
    this.productId = this.getActualProductId();
    this.apiBase = PRODUCT_RENTAL_API_BASE_URL;
    this.isProcessingReplacement = false;
    
    console.log('📋 ProductRental instance created:', {
      variantId: this.variantId,
      customerId: this.customerId,
      productId: this.productId,
      apiBase: this.apiBase
    });
    
    // Debug: See what images are available on the page
    this.debugPageImages();
    
    this.init();
  }

  getActualProductId() {
    // Find the actual rental button on the page and extract its product ID
    const rentalButton = document.querySelector('[id*="rental-button-"]');
    if (rentalButton) {
      const buttonId = rentalButton.id;
      const productId = buttonId.replace('rental-button-', '');
      console.log('🔍 Found actual product ID from button:', productId);
      return productId;
    }
    
    // If button not found, try to wait a bit for it to load
    console.log('⏳ Button not found immediately, waiting for DOM...');
    const buttons = document.querySelectorAll('[id*="rental-button"]');
    if (buttons.length > 0) {
      const buttonId = buttons[0].id;
      const productId = buttonId.replace('rental-button-', '');
      console.log('🔍 Found product ID after waiting:', productId);
      return productId;
    }
    
    // Fallback to the old method if no button found
    const extractedId = this.variantId.split('-')[0] || this.variantId;
    console.log('⚠️ No button found, using fallback product ID:', extractedId);
    return extractedId;
  }

  async init() {
    console.log('🔄 ProductRental.init() started for product:', this.productId);
    
    try {
    await this.updateRentalStatus();
    this.attachEventListeners();
      this.checkCartState();
      
      // Add a final check to ensure button text is clean
      this.ensureCleanButtonText();
      
      console.log('✅ ProductRental.init() completed successfully');
    } catch (error) {
      console.error('❌ ProductRental.init() failed:', error);
    }
  }

  // Final button check to ensure no slot information is displayed
  ensureCleanButtonText() {
    // Add a small delay to ensure this runs after any other code
    setTimeout(() => {
      try {
        console.log('🔧 Running final button text check...');
        const rentalButton = document.getElementById(`rental-button-${this.productId}`);
        const buttonText = rentalButton?.querySelector('.rental-button__text');
        
        if (buttonText) {
          const currentText = buttonText.textContent;
          console.log('🔍 Current button text:', currentText);
          
          // Force button text to be clean
          if (currentText.includes('slot') || currentText.includes('(') || /\d/.test(currentText)) {
            console.log('🔧 FORCE FIXING button text that contains slot information');
            buttonText.textContent = 'Rent Now';
          }
        }
      } catch (error) {
        console.error('❌ Error in ensureCleanButtonText:', error);
      }
    }, 500);
  }

  checkCartState() {
    console.log('🛒 Checking cart state for variant:', this.variantId);
    
    if (typeof window.rentalCart === 'undefined') {
      console.warn('⚠️ rentalCart not available yet');
      return;
    }
    
    if (typeof window.rentalCart.isBookInCart !== 'function') {
      console.warn('⚠️ rentalCart.isBookInCart method not available');
      return;
    }
    
    const isInCart = window.rentalCart.isBookInCart(this.variantId);
    console.log('📚 Book in cart status:', isInCart);
    
    if (isInCart) {
      console.log('📚 Book already in cart, updating button state');
      this.updateButtonToInCart();
    }
  }

  async updateRentalStatus() {
    console.log('🔄 updateRentalStatus() started for variant:', this.variantId);
    console.log('👤 Customer ID:', this.customerId);
    console.log('🌐 API URL:', `${this.apiBase}/product-status/${this.variantId}?customer_id=${this.customerId}`);
    
    // Update UI to show loading state immediately
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    const buttonText = rentalButton?.querySelector('.rental-button__text');
    if (buttonText) {
      buttonText.textContent = 'Loading...';
    }
    if (rentalButton) {
      rentalButton.classList.add('loading');
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ API request timeout after 5 seconds');
        controller.abort();
      }, 5000);

      console.log('📡 Making API request...');
      const response = await fetch(
        `${this.apiBase}/product-status/${this.variantId}?customer_id=${this.customerId}`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      console.log('📡 API response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const status = await response.json();
      console.log('✅ Rental status received:', status);
      this.updateButtonDisplay(status);
    } catch (error) {
      console.error('❌ Error updating rental status:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      if (error.name === 'AbortError') {
        console.log('⏰ Showing timeout state');
        this.showTimeoutState();
      } else {
        console.log('❌ Showing error state');
        this.showErrorState();
      }
      
      console.log('🔄 Showing fallback button state');
      this.showFallbackButton();
    }
  }

  updateButtonDisplay(status) {
    console.log('🎨 updateButtonDisplay() called with status:', status);
    console.log('🔍 RAW BUTTON TEXT FROM API:', status.buttonText);
    
    // FORCE FIX: Override any slots text in status to ensure clean text
    if (status.buttonText && (status.buttonText.includes('slot') || status.buttonText.includes('('))) {
      console.log('🔧 FORCE FIX: Overriding API button text that contains slot information');
      status.buttonText = 'Rent Now';
    }
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    const replaceButton = document.getElementById(`replace-button-${this.productId}`);
    const rentalStatus = document.getElementById(`rental-status-${this.productId}`);

    console.log('🔍 Button elements found:', {
      rentalButton: !!rentalButton,
      replaceButton: !!replaceButton,
      rentalStatus: !!rentalStatus,
      productId: this.productId
    });

    // handle case where the variant itself doesn't exist
    if (status.variantExists === false) {
      console.warn('⚠️ Variant does not appear to exist, but proceeding anyway');
      if (rentalStatus) {
        rentalStatus.style.display = 'block';
        const textElement = rentalStatus.querySelector('.rental-status__text');
        if (textElement) {
          textElement.textContent = '⚠️ Book status uncertain, proceed to checkout';
        }
      }
      // do not disable the button; allow user to add to cart and attempt
      // checkout. earlier code used `return` here, but now we fall through
      // so the rest of the display logic can still run.
    }

    if (!rentalButton) {
      console.warn('⚠️ Rental button not found for product:', this.productId);
      console.warn('🔍 Available buttons on page:', document.querySelectorAll('[id*="rental-button"]'));
      return;
    }

    console.log('🧹 Removing loading state from rental button');
    rentalButton.classList.remove('loading');
    
    // Handle inventory availability and button state
    const isOutOfStock = status.inventoryAvailable === false;
    const isDisabled = status.buttonDisabled || isOutOfStock;
    
    console.log('📦 Inventory check:', {
      inventoryAvailable: status.inventoryAvailable,
      inventoryQuantity: status.inventoryQuantity,
      isOutOfStock,
      buttonDisabled: status.buttonDisabled,
      finalDisabled: isDisabled
    });

    // Always show the Rent Now button (cart handles slot management)
    console.log('📚 Showing rental button');
    rentalButton.style.display = 'block';
    if (replaceButton) {
      replaceButton.style.display = 'none';
    }
    
    // Only disable for out of stock
    rentalButton.disabled = isOutOfStock;
    
    if (isOutOfStock) {
      console.log('❌ Setting button to out of stock state');
      rentalButton.classList.remove('active');
      rentalButton.classList.add('disabled', 'out-of-stock');
    } else {
      console.log('✅ Setting button to active state');
      rentalButton.classList.add('active');
      rentalButton.classList.remove('disabled', 'out-of-stock');
    }

    // Update rental status message
    if (rentalStatus) {
      if (isOutOfStock) {
        console.log('📋 Showing out of stock status');
        rentalStatus.style.display = 'block';
        const textElement = rentalStatus.querySelector('.rental-status__text');
        if (textElement) {
          textElement.textContent = 'This book is currently out of stock';
        }
      } else {
        console.log('📋 Hiding rental status');
        rentalStatus.style.display = 'none';
      }
    }

    // Update button text using the API response or fall back to logic-based text
      const buttonText = rentalButton.querySelector('.rental-button__text');
      if (buttonText) {
      if (status.buttonText) {
        // Use the text from the API response but ensure no slot info is included
        // Remove any slot information from the button text
        let cleanButtonText = status.buttonText;
        
        // More aggressive filtering - remove any text with slots, numbers, or parentheses
        if (cleanButtonText.includes('slot') || 
            cleanButtonText.includes('(') || 
            /\d/.test(cleanButtonText)) {
          cleanButtonText = 'Rent Now';
        }
        
        buttonText.textContent = cleanButtonText;
        console.log('📝 Button text set from API (cleaned):', cleanButtonText);
      } else {
        // Fallback to client-side logic
        if (isOutOfStock) {
          buttonText.textContent = 'Out of Stock';
        } else {
          buttonText.textContent = 'Rent Now';
        }
        console.log('📝 Button text set from fallback logic:', buttonText.textContent);
    }
  }

    console.log('✅ Button display updated successfully');
  }

  attachEventListeners() {
    console.log('🔗 attachEventListeners() called for product:', this.productId);
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    console.log('🔍 Rental button found for event listener:', !!rentalButton);
    
    if (rentalButton) {
      rentalButton.addEventListener('click', (e) => {
        console.log('🖱️ Rental button clicked for product:', this.productId);
        e.preventDefault();
        this.handleRental();
      });
      console.log('✅ Rental button click listener attached');
    } else {
      console.warn('⚠️ Rental button not found for event listener');
    }

    // Replace button removed - cart handles slot management and returns
  }

  async handleRental() {
    try {
      console.log('🛒 Handling rental for variant:', this.variantId);
      
      // Check if the button is disabled (out of stock or other reasons)
      const rentalButton = document.getElementById(`rental-button-${this.productId}`);
      if (rentalButton && rentalButton.disabled) {
        console.log('❌ Rental button is disabled, preventing rental attempt');
        
        if (rentalButton.classList.contains('out-of-stock')) {
          this.showError('This book is currently out of stock.');
        } else {
          this.showError('This book cannot be rented at the moment.');
        }
        return;
      }
      
      const bookData = await this.getBookData();
      if (!bookData) {
        this.showError('Could not get book information. Please try again.');
        return;
      }

      if (typeof window.rentalCart === 'undefined') {
        console.warn('⚠️ Rental cart not available, trying to initialize...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (typeof window.rentalCart === 'undefined') {
          this.showError('Rental cart system not loaded. Please refresh the page.');
          return;
        }
      }

      console.log('📚 Adding book to rental cart:', bookData);
      
      const success = await window.rentalCart.addToCart(bookData);
      
      if (success) {
        this.updateButtonToInCart();
        document.dispatchEvent(new Event('rentalCartUpdate'));
        console.log('✅ Book successfully added to rental cart');
      } else {
        console.warn('⚠️ Failed to add book to cart');
      }
    } catch (error) {
      console.error('❌ Error adding book to rental cart:', error);
      this.showError('Error adding book to cart. Please try again.');
    }
  }

  async getBookData() {
    try {
      let productTitle = document.querySelector('.product-title, h1, .product__title')?.textContent;
      
      // Clean the title to remove extra whitespace and duplicates
      if (productTitle) {
        // Remove extra whitespace and normalize
        productTitle = productTitle.replace(/\s+/g, ' ').trim();
        
        // Check if title contains duplicate text (common issue with some themes)
        const words = productTitle.split(' ');
        if (words.length > 2) {
          const firstHalf = words.slice(0, Math.ceil(words.length / 2)).join(' ');
          const secondHalf = words.slice(Math.ceil(words.length / 2)).join(' ');
          
          // If the two halves are very similar, use just the first half
          if (firstHalf.toLowerCase() === secondHalf.toLowerCase()) {
            productTitle = firstHalf;
            console.log('🔧 Detected duplicate title, using first half:', firstHalf);
          }
        }
      }
      
      // Try multiple selectors to find the product image
      let productImage = null;
      
      // Method 1: Try common product image selectors
      const imageSelectors = [
        '.product-image img',
        '.product__image img',
        '.product-single__photo img',
        '.product__media img',
        '.product-gallery img',
        '.product-thumbnail img',
        '.product-main-image img',
        '.product-image-container img',
        '.product__photo img',
        '.product-single__media img',
        '.product__gallery img',
        '.product__thumbnail img',
        '.product__main-image img',
        '.product__image-container img'
      ];
      
      for (const selector of imageSelectors) {
        const img = document.querySelector(selector);
        if (img && img.src && img.src.trim() !== '') {
          productImage = img.src;
          console.log('📸 Found product image using selector:', selector, productImage);
          break;
        }
      }
      
      // Method 2: If no image found, try to find any img tag within product containers
      if (!productImage) {
        const productContainers = document.querySelectorAll('.product, .product-single, .product__container, .product-container');
        for (const container of productContainers) {
          const img = container.querySelector('img');
          if (img && img.src && img.src.trim() !== '' && !img.src.includes('placeholder')) {
            productImage = img.src;
            console.log('📸 Found product image in container:', productImage);
            break;
          }
        }
      }
      
      // Method 3: Look for any image with 'product' in its class or ID
      if (!productImage) {
        const allImages = document.querySelectorAll('img');
        for (const img of allImages) {
          const imgClasses = img.className || '';
          const imgId = img.id || '';
          if ((imgClasses.includes('product') || imgId.includes('product')) && 
              img.src && img.src.trim() !== '' && 
              !img.src.includes('placeholder') &&
              img.src.includes('cdn.shopify.com')) {
            productImage = img.src;
            console.log('📸 Found product image by class/id:', productImage);
            break;
          }
        }
      }
      
      console.log('📚 Book data collected:', {
        title: productTitle,
        titleLength: productTitle?.length,
        image: productImage,
        variantId: this.variantId,
        productId: this.productId
      });
      
      // Debug: Log the raw title content to see what's happening
      if (productTitle) {
        console.log('🔍 Raw title content:', JSON.stringify(productTitle));
        console.log('🔍 Title characters:', Array.from(productTitle).map(char => char.charCodeAt(0)));
      }
      
      return {
        variantId: this.variantId,
        productId: this.productId, // Add productId for image fetching
        title: productTitle || 'Unknown Book',
        image_url: productImage || null
      };
    } catch (error) {
      console.error('Error getting book data:', error);
      return null;
    }
  }

  updateButtonToInCart() {
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    if (rentalButton) {
      rentalButton.innerHTML = `
        <span class="rental-button__text">✓ In Cart</span>
        <span class="rental-button__icon">🛒</span>
      `;
      rentalButton.classList.remove('active');
      rentalButton.classList.add('in-cart');
      rentalButton.disabled = true;
      rentalButton.title = 'This book is already in your rental cart';
    }
  }

  showError(message) {
    const toast = document.createElement('div');
    toast.className = 'rental-error-toast';
    toast.textContent = message;
    
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      background: '#e74c3c',
      color: 'white',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });

    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }

  showSuccess(message) {
    const toast = document.createElement('div');
    toast.className = 'rental-success-toast';
    toast.textContent = message;
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      background: '#2ecc71',
      color: 'white',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.transform = 'translateX(0)'; }, 100);
    setTimeout(() => { toast.style.transform = 'translateX(100%)'; setTimeout(() => toast.remove(), 300); }, 3000);
  }

  showWarning(message) {
    const toast = document.createElement('div');
    toast.className = 'rental-warning-toast';
    toast.textContent = message;
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      background: '#f39c12',
      color: 'white',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.transform = 'translateX(0)'; }, 100);
    setTimeout(() => { toast.style.transform = 'translateX(100%)'; setTimeout(() => toast.remove(), 300); }, 3000);
  }

  // Replace modal removed - cart handles slot management and returns

  // selectReplaceOption removed - cart handles slot management and returns

  // Placeholder to avoid breaking references - these methods are no longer used
  closeReplaceModal() {}
  reEnableReplaceButtons() {}

  showErrorState() {
    console.log('❌ showErrorState() called for product:', this.productId);
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    if (rentalButton) {
      rentalButton.disabled = true;
      rentalButton.classList.remove('loading');
      rentalButton.classList.add('error');
      
      const buttonText = rentalButton.querySelector('.rental-button__text');
      if (buttonText) {
        buttonText.textContent = 'Error Loading Status';
        console.log('📝 Error state button text set');
      }
      
      console.log('✅ Error state applied to button');
    } else {
      console.error('❌ Error state button not found');
    }
  }

  showTimeoutState() {
    console.log('⏰ showTimeoutState() called for product:', this.productId);
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    if (rentalButton) {
      rentalButton.disabled = false;
      rentalButton.classList.remove('loading');
      rentalButton.classList.add('error');
      
      const buttonText = rentalButton.querySelector('.rental-button__text');
      if (buttonText) {
        buttonText.textContent = 'Request Timeout - Click to Retry';
        console.log('📝 Timeout state button text set');
      }
      
      rentalButton.onclick = (e) => {
        e.preventDefault();
        console.log('🔄 Retry clicked, calling retryRentalStatus');
        this.retryRentalStatus();
      };
      
      console.log('✅ Timeout state applied to button');
    } else {
      console.error('❌ Timeout state button not found');
    }
  }

  showFallbackButton() {
    console.log('🔄 showFallbackButton() called for product:', this.productId);
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    console.log('🔍 Fallback button element found:', !!rentalButton);
    
    if (rentalButton) {
      console.log('🧹 Removing loading state from fallback button');
      rentalButton.classList.remove('loading');
      rentalButton.disabled = false;
      
      const buttonText = rentalButton.querySelector('.rental-button__text');
      if (buttonText) {
        buttonText.textContent = 'Rent Now';
        console.log('📝 Fallback button text set to:', buttonText.textContent);
      } else {
        console.warn('⚠️ Button text element not found in fallback button');
      }
      
      rentalButton.classList.add('active');
      console.log('✅ Fallback button state applied successfully');
    } else {
      console.error('❌ Fallback button not found for product:', this.productId);
      console.error('🔍 Available buttons:', document.querySelectorAll('[id*="rental-button"]'));
    }
  }

  async retryRentalStatus() {
    console.log('🔄 retryRentalStatus() called for product:', this.productId);
    
    const rentalButton = document.getElementById(`rental-button-${this.productId}`);
    if (rentalButton) {
      console.log('🧹 Resetting button to loading state for retry');
      rentalButton.classList.remove('error');
      rentalButton.classList.add('loading');
      
      const buttonText = rentalButton.querySelector('.rental-button__text');
      if (buttonText) {
        buttonText.textContent = 'Loading...';
        console.log('📝 Retry button text set to Loading...');
      }
      
      rentalButton.disabled = true;
      rentalButton.onclick = null;
      console.log('🔄 Restored original click handler');
      
      console.log('🔄 Retrying rental status update...');
      await this.updateRentalStatus();
    } else {
      console.error('❌ Retry button not found');
    }
  }

  // Debug method to see what images are available on the page
  debugPageImages() {
    console.log('🔍 Debugging page images...');
    
    const allImages = document.querySelectorAll('img');
    console.log(`📸 Found ${allImages.length} images on the page`);
    
    allImages.forEach((img, index) => {
      if (img.src && img.src.trim() !== '') {
        console.log(`Image ${index + 1}:`, {
          src: img.src,
          alt: img.alt,
          className: img.className,
          id: img.id,
          width: img.width,
          height: img.height
        });
      }
    });
    
    // Also check for product-specific containers
    const productContainers = document.querySelectorAll('[class*="product"], [id*="product"]');
    console.log(`🏪 Found ${productContainers.length} product-related containers:`, 
      Array.from(productContainers).map(el => ({ className: el.className, id: el.id }))
    );
  }
};

// Global initialization function
window.initializeRentalButton = function(productId, variantId, customerId) {
  console.log('🚀 initializeRentalButton called with:', { productId, variantId, customerId });
  // Pass variantId and customerId as intended, productId will be extracted from the button
  return new window.ProductRental(variantId, customerId);
};

// Global debugging function
window.debugRentalSystem = function() {
  console.log('🔍 === RENTAL SYSTEM DEBUG INFO ===');
  console.log('🌐 API Base URL:', PRODUCT_RENTAL_API_BASE_URL);
  console.log('📚 ProductRental class available:', typeof window.ProductRental);
  console.log('🛒 rentalCart available:', typeof window.rentalCart);
  
  if (window.rentalCart) {
    console.log('🛒 rentalCart details:', {
      cart: window.rentalCart.getCart ? window.rentalCart.getCart() : 'No getCart method',
      count: window.rentalCart.getCartCount ? window.rentalCart.getCartCount() : 'No getCartCount method',
      customerId: window.rentalCart.customerId,
      customerPlan: window.rentalCart.customerPlan
    });
  }
  
  const rentalButtons = document.querySelectorAll('[id*="rental-button"]');
  console.log('🔘 Rental buttons found on page:', rentalButtons.length);
  rentalButtons.forEach((btn, index) => {
    console.log(`🔘 Button ${index + 1}:`, {
      id: btn.id,
      text: btn.textContent?.trim(),
      classes: btn.className,
      disabled: btn.disabled,
      display: btn.style.display
    });
  });
  
  const loadingButtons = document.querySelectorAll('.loading');
  console.log('⏳ Buttons with loading class:', loadingButtons.length);
  
  console.log('🔍 === END DEBUG INFO ===');
};

// Global function to clean all rental buttons on the page
window.forceCleanAllRentalButtons = function() {
  console.log('🚨 RUNNING GLOBAL BUTTON CLEANER');
  
  // Run immediately and also after a delay
  cleanButtons();
  setTimeout(cleanButtons, 1000);
  
  function cleanButtons() {
    try {
      // Find all rental buttons on the page
      const rentalButtons = document.querySelectorAll('[id*="rental-button"]');
      console.log(`🔍 Found ${rentalButtons.length} rental buttons on page`);
      
      rentalButtons.forEach(button => {
        const buttonText = button.querySelector('.rental-button__text');
        if (buttonText) {
          const currentText = buttonText.textContent;
          
          // Check if it contains slot information
          if (currentText.includes('slot') || currentText.includes('(') || /\d/.test(currentText)) {
            console.log(`🔧 FORCE CLEANING button text: "${currentText}" to "Rent Now"`);
            buttonText.textContent = 'Rent Now';
          }
        }
      });
    } catch (error) {
      console.error('❌ Error in global button cleaner:', error);
    }
  }
};

// Auto-debug on page load
document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 ProductRental.js DOM ready, running auto-debug...');
  setTimeout(() => {
    if (typeof window.debugRentalSystem === 'function') {
      window.debugRentalSystem();
    }
    
    // Run the button cleaner to force clean any buttons with slot information
    if (typeof window.forceCleanAllRentalButtons === 'function') {
      window.forceCleanAllRentalButtons();
    }
  }, 2000);
  
  // Also run it periodically to catch any dynamic updates
  setInterval(() => {
    if (typeof window.forceCleanAllRentalButtons === 'function') {
      window.forceCleanAllRentalButtons();
    }
  }, 5000);
});

console.log('✅ ProductRental.js loaded successfully');